import { Form, Formik } from 'formik';
import { useRef, useState } from 'react';
import {
  DialogBody,
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@src/design-system/dialog';
import { Button } from '@src/design-system/button';
import { useDialogContext } from '@src/design-system/dialog/use-dialog-context';
import { showToast } from '@src/shared/utils/helpers/show-toast';
import type { TAnnouncement } from '@src/shared/types/announcement';
import announcementsApi from '../../services/announcements-api';
import { uploadAnnouncementMedia } from '../../utils/upload-announcement-media';
import { buildAnnouncementAudiencePayload } from '../../utils/announcement-audience';
import { AnnouncementPreview } from './announcement-preview';
import {
  announcementFormSchema,
  createAnnouncementInitialValues,
  type TAnnouncementFormValues,
} from './schema';
import { AnnouncementInfoSection } from './sections/info-section';
import { AnnouncementMediaSection } from './sections/media-section';
import { AnnouncementCtaSection } from './sections/cta-section';
import { AnnouncementAudienceSection } from './sections/audience-section';
import { AnnouncementScheduleSection } from './sections/schedule-section';

type CreateOrEditAnnouncementDialogProps = {
  announcement?: TAnnouncement;
};

export function CreateOrEditAnnouncementDialog({
  announcement,
}: CreateOrEditAnnouncementDialogProps) {
  const { formikRef } = useDialogContext();
  const closeRef = useRef<HTMLButtonElement>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const { mutateAsync: createAnnouncement, isPending: isCreating } =
    announcementsApi.useCreate();
  const { mutateAsync: updateAnnouncement, isPending: isUpdating } =
    announcementsApi.useUpdate();
  const { mutateAsync: attachMedia, isPending: isAttaching } =
    announcementsApi.useAttachMedia();
  const { mutateAsync: replaceMedia, isPending: isReplacing } =
    announcementsApi.useReplaceMedia();

  const isSubmitting =
    isCreating ||
    isUpdating ||
    isAttaching ||
    isReplacing ||
    uploadProgress !== null;

  const handleSubmit = async (values: TAnnouncementFormValues) => {
    try {
      let mediaKey: string | undefined;

      if (values.mediaFile) {
        abortControllerRef.current = new AbortController();
        setUploadProgress(0);
        mediaKey = await uploadAnnouncementMedia(values.mediaFile, {
          onProgress: setUploadProgress,
          signal: abortControllerRef.current.signal,
        });
      }

      const audiencePayload = buildAnnouncementAudiencePayload(values);

      const schedulePayload = values.scheduleShare
        ? {
            status: 'SCHEDULED' as const,
            share_at: values.shared_at,
            expire_at: values.expire_at,
          }
        : {
            status: 'LIVE' as const,
          };

      const payload = {
        name: values.name,
        description: values.description || '',
        cta_label: values.showCta ? values.cta_label : '',
        cta_link: values.showCta ? values.cta_link : '',
        is_active: values.is_active,
        ...audiencePayload,
        ...schedulePayload,
      };

      if (announcement?.id) {
        await updateAnnouncement({
          id: announcement.id,
          ...payload,
        });

        if (mediaKey) {
          if (announcement.media) {
            await replaceMedia({ id: announcement.id, key: mediaKey });
          } else {
            await attachMedia({ id: announcement.id, key: mediaKey });
          }
        }
      } else {
        if (!mediaKey && !values.existingMedia) {
          showToast({
            title: 'Media required',
            description:
              'Please upload an image or video for the announcement.',
            variant: 'error',
          });
          return;
        }

        await createAnnouncement({
          ...payload,
          ...(mediaKey ? { key: mediaKey } : {}),
        });
      }

      closeRef.current?.click();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      const message =
        error instanceof Error ? error.message : 'Failed to save announcement';
      showToast({
        title: 'Error',
        description: message,
        variant: 'error',
      });
    } finally {
      setUploadProgress(null);
      abortControllerRef.current = null;
    }
  };

  return (
    <>
      <DialogHeader className='border-b border-primary-100 px-4 py-5'>
        <DialogTitle className='text-base'>
          {announcement ? 'Edit Announcement' : 'Add Announcement'}
        </DialogTitle>
      </DialogHeader>

      <Formik
        innerRef={formikRef}
        initialValues={createAnnouncementInitialValues(announcement)}
        validationSchema={announcementFormSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values }) => (
          <Form className='flex flex-1 flex-col overflow-hidden'>
            <DialogBody className='flex min-h-0 flex-1 overflow-hidden p-0'>
              <div className='flex w-1/2 flex-col border-r border-primary-100'>
                <div className='flex-1 space-y-6 overflow-y-auto p-4'>
                  <AnnouncementInfoSection />
                  <AnnouncementMediaSection />
                  <AnnouncementCtaSection />
                  <AnnouncementAudienceSection />
                  <AnnouncementScheduleSection />
                </div>

                <DialogFooter className='mt-auto shrink-0 border-t border-primary-100'>
                  <Button
                    type='submit'
                    variant='primary'
                    className='h-12 flex-1 rounded-xl'
                    disabled={isSubmitting}
                    isLoading={isSubmitting}
                  >
                    {uploadProgress !== null
                      ? `Uploading ${uploadProgress}%`
                      : 'Confirm'}
                  </Button>
                  <DialogClose asChild>
                    <Button
                      type='button'
                      variant='primaryOutline'
                      className='h-12 flex-1 rounded-xl'
                      disabled={isSubmitting}
                      onClick={() => abortControllerRef.current?.abort()}
                    >
                      Cancel
                    </Button>
                  </DialogClose>
                  <DialogClose
                    ref={closeRef}
                    className='hidden'
                  />
                </DialogFooter>
              </div>

              <div className='h-full w-1/2 overflow-y-auto'>
                <AnnouncementPreview values={values} />
              </div>
            </DialogBody>
          </Form>
        )}
      </Formik>
    </>
  );
}
